import React, { useEffect, useState } from 'react';
import { Trophy, Info, Medal, List } from 'lucide-react';
import API_URL from '../../config';

import SEO from './SEO';

export default function PageVysledky() {
    const currentYear = new Date().getFullYear().toString();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [historicalData, setHistoricalData] = useState({});
    const [yearLinks, setYearLinks] = useState({});
    const [dbResults, setDbResults] = useState([]);
    const [dbMetadata, setDbMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalImg, setModalImg] = useState(null);

    const years = [];
    for (let y = parseInt(currentYear); y >= 2017; y--) {
        years.push(y.toString());
    }
    const isDBYear = parseInt(selectedYear) >= 2025;

    const formatYearShort = (y) => {
        const year = parseInt(y);
        if (year === 2017) return '2017 (0.r)';
        return `${year} (${year - 2016}.r)`;
    };

    const medalStyle = (idx) => {
        if (idx === 0) return 'bg-yellow-400 text-yellow-900 border border-yellow-500';
        if (idx === 1) return 'bg-gray-300 text-gray-700 border border-gray-400';
        if (idx === 2) return 'bg-orange-300 text-orange-900 border border-orange-400';
        return 'bg-blue-100 text-blue-800 border border-blue-200';
    };

    useEffect(() => {
        fetch(`${API_URL}/historical-results`).then(r => r.json()).then(setHistoricalData).catch(() => {});
        fetch(`${API_URL}/year-overall-links`).then(r => r.json()).then(data => {
            const map = {};
            data.forEach(l => { map[l.year] = l.image_url; });
            setYearLinks(map);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        fetch(`${API_URL}/results?year=${selectedYear}`)
            .then(r => r.json())
            .then(data => {
                if (data.results) {
                    setDbResults(data.results);
                    setDbMetadata(data.metadata);
                } else {
                    setDbResults(Array.isArray(data) ? data : []);
                    setDbMetadata(null);
                }
                setLoading(false);
            })
            .catch(() => {
                setDbResults([]);
                setDbMetadata(null);
                setLoading(false);
            });
    }, [selectedYear]);

    const histYearData = historicalData[selectedYear] || [];
    const byCategory = histYearData.reduce((acc, res) => {
        if (!acc[res.category]) acc[res.category] = [];
        acc[res.category].push(res);
        return acc;
    }, {});

    const totalHistoricalBoats = histYearData.length;
    let totalHistoricalParticipants = 0;
    histYearData.forEach(res => {
        if (Array.isArray(res.names)) {
            totalHistoricalParticipants += res.names.length;
        } else if (typeof res.names === 'string') {
            totalHistoricalParticipants += res.names.split(',').filter(n => n.trim().length > 0).length;
        }
    });

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 min-h-screen">
            <SEO 
                title="Výsledky a Archív | Malodunajský vodácky maratón"
                description="Kompletné výsledky aktuálneho ročníka aj historický archív víťazov Malodunajského vodáckeho maratónu."
                keywords="výsledky maratónu, archív pretekov, víťazi splavu, časy pretekárov"
            />
            <h1 className="text-3xl md:text-5xl font-black uppercase tracking-widest flex items-center gap-4 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Trophy className="w-10 h-10 md:w-12 md:h-12 text-yellow-400" />
                <span className="text-white drop-shadow-md">Výsledkové Listiny</span>
            </h1>

            <div className="bg-white shadow-2xl rounded-[2.5rem] overflow-hidden border border-gray-100 mb-20">
                {/* Year selector header as TABS */}
                <div className="bg-gray-100 p-4 md:p-6 border-b border-gray-200">
                    <h3 className="text-blue-900 font-black text-xs uppercase tracking-widest mb-4 px-2 opacity-50" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vyberte ročník</h3>
                    <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar px-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {years.map(y => {
                            const isActive = selectedYear === y;
                            return (
                                <button
                                    key={y}
                                    onClick={() => setSelectedYear(y)}
                                    className={`shrink-0 px-6 py-3 rounded-2xl font-black text-sm uppercase tracking-wider transition-all border-2 ${
                                        isActive 
                                        ? 'bg-blue-900 border-blue-900 text-white shadow-lg scale-105' 
                                        : 'bg-white border-white/50 text-blue-900 hover:border-blue-900 hover:bg-blue-50 shadow-sm'
                                    }`}
                                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                                >
                                    {formatYearShort(y)}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Results body */}
                <div className="p-4 md:p-10 min-h-[400px] bg-gray-50">
                    <div className="w-full">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-4">
                                <div className="w-12 h-12 border-4 border-blue-900 border-t-yellow-400 rounded-full animate-spin" />
                                <p className="text-gray-500 font-bold">Načítavam výsledky...</p>
                            </div>
                        ) : (
                            <>
                                {/* SECTION 0: YEAR STATS & NOTE */}
                                {dbMetadata && (
                                    <div className="mb-12 bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-blue-900/5 border border-blue-100 flex flex-col md:flex-row gap-8 items-start md:items-center">
                                        <div className="flex gap-6 shrink-0">
                                            <div className="text-center">
                                                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Účastníci</p>
                                                <p className="text-3xl font-black text-blue-900 leading-none">{dbMetadata.participant_count || 0}</p>
                                            </div>
                                            <div className="w-px h-10 bg-gray-200 self-center" />
                                            <div className="text-center">
                                                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Posádky</p>
                                                <p className="text-3xl font-black text-blue-900 leading-none">{dbMetadata.boat_count || 0}</p>
                                            </div>
                                        </div>
                                        
                                        {dbMetadata.note && (
                                            <div className="flex-grow bg-blue-50/50 rounded-2xl p-4 border border-blue-100/50 flex items-start gap-3">
                                                <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                                <p className="text-sm font-bold text-blue-800 leading-relaxed italic">
                                                    {dbMetadata.note}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isDBYear ? (
                                    <>
                                        {/* SECTION 1: TOP 3 WINNERS BY CATEGORY */}
                                        <div className="mb-16">
                                            <div className="flex items-center gap-4 mb-10 pb-4 border-b-2 border-gray-100">
                                                <div className="p-3 bg-blue-50 rounded-2xl">
                                                    <Trophy className="w-8 h-8 text-blue-900" />
                                                </div>
                                                <div>
                                                    <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-blue-900 leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                        Najlepšie výsledky podľa kategórií
                                                    </h3>
                                                    <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">
                                                        Víťazi jednotlivých kategórií (prvé 3 miesta)
                                                    </p>
                                                </div>
                                            </div>

                                            {dbResults.map(marathonType => (
                                                <div key={`winners-${marathonType.id}`} className="mb-12 last:mb-0">
                                                    <h4 className="text-lg font-black uppercase tracking-tight text-blue-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                        <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                                                        {marathonType.name}
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                                        {marathonType.categories.map(cat => {
                                                            const displayedCrews = marathonType.id === 3 ? cat.crews : cat.crews.slice(0, 3);
                                                            return (
                                                                <div key={cat.id} className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-gray-100 overflow-hidden flex flex-col group transition-all duration-300 hover:shadow-2xl hover:shadow-blue-900/10">
                                                                    <div className="bg-blue-900 py-3 px-6 text-center border-b-2 border-yellow-400">
                                                                        <h5 className="text-white font-black uppercase tracking-widest text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>{cat.name}</h5>
                                                                    </div>
                                                                    <div className="p-4 md:p-5 flex flex-col gap-4">
                                                                        {displayedCrews.map((crew, idx) => (
                                                                            <div key={crew.id} className="flex items-center gap-3">
                                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black shrink-0 shadow-sm text-xs ${medalStyle(idx)}`}>
                                                                                    {idx + 1}
                                                                                </div>
                                                                                <div className="flex flex-col flex-grow min-w-0 pr-2">
                                                                                    {crew.participants?.map((p, pi) => (
                                                                                        <span key={pi} className="text-blue-900 font-bold text-sm md:text-xs leading-tight block truncate">
                                                                                            {p.name}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                                <div className="bg-blue-50 text-blue-900 font-black text-[10px] px-2 py-1 rounded-lg border border-blue-100 tabular-nums shrink-0">
                                                                                    {crew.race_time?.total_time}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* SECTION 2: OVERALL STANDINGS */}
                                        <div className="mb-16">
                                            <div className="flex items-center gap-4 mb-10 pb-4 border-b-2 border-gray-100">
                                                <div className="p-3 bg-blue-50 rounded-2xl">
                                                    <List className="w-8 h-8 text-blue-900" />
                                                </div>
                                                <div>
                                                    <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-blue-900 leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                        Celkové poradia
                                                    </h3>
                                                    <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">
                                                        Kompletné výsledky podľa typu maratónu
                                                    </p>
                                                </div>
                                            </div>

                                            {dbResults.map(marathonType => (
                                                <div key={`overall-${marathonType.id}`} className="mb-12 last:mb-0">
                                                     <h4 className="text-lg font-black uppercase tracking-tight text-blue-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                        <Medal className="w-6 h-6 text-yellow-500" />
                                                        {marathonType.name}
                                                    </h4>
                                                    
                                                    <div className="hidden md:block bg-white rounded-[2rem] shadow-2xl shadow-blue-900/5 border border-gray-100 overflow-hidden">
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-left border-collapse">
                                                                <thead>
                                                                    <tr className="bg-gray-50/50 text-gray-400 font-black uppercase tracking-widest text-[9px]">
                                                                        <th className="px-8 py-4 w-20">Por.</th>
                                                                        <th className="px-8 py-4">Účastníci</th>
                                                                        <th className="px-8 py-4">Kategória</th>
                                                                        <th className="px-8 py-4 text-right">Čas</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="text-gray-800 font-medium whitespace-nowrap">
                                                                    {marathonType.overall.map((crew, i) => (
                                                                        <tr key={crew.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors group">
                                                                            <td className="px-8 py-4">
                                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs transition-transform group-hover:scale-110 ${medalStyle(i)}`}>
                                                                                    {i + 1}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-8 py-4">
                                                                                <div className="flex flex-col">
                                                                                    {crew.participants?.map((p, pi) => (
                                                                                        <span key={pi} className="text-blue-900 font-bold block leading-tight text-xs">
                                                                                            {p.name}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-8 py-4">
                                                                                <span className="bg-blue-50 text-blue-800 px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border border-blue-100">
                                                                                    {crew.category?.name}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-8 py-4 text-right font-black text-gray-900 tabular-nums text-xs">
                                                                                {crew.race_time?.total_time}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>

                                                    <div className="md:hidden flex flex-col gap-4">
                                                        {marathonType.overall.map((crew, i) => (
                                                            <div key={`mob-${crew.id}`} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black shrink-0 text-sm ${medalStyle(i)}`}>
                                                                    {i + 1}
                                                                </div>
                                                                <div className="flex-grow min-w-0">
                                                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mb-1">
                                                                        {crew.participants?.map((p, pi) => (
                                                                            <span key={pi} className="text-blue-900 font-bold text-sm leading-tight inline-block capitalize">
                                                                                {p.name}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[10px] font-black uppercase tracking-widest text-blue-400 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                                                            {crew.category?.name}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right shrink-0">
                                                                    <div className="text-blue-900 font-black text-sm tabular-nums whitespace-nowrap">
                                                                        {crew.race_time?.total_time}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    /* Historical year results */
                                    <div className="flex flex-col items-center">
                                        {histYearData.length === 0 ? (
                                            <p className="text-center text-gray-500 font-bold py-12">Výsledky pre ročník {selectedYear} nie sú k dispozícii.</p>
                                        ) : (
                                            <>
                                                <div className="mb-12 bg-white rounded-3xl p-6 shadow-xl shadow-blue-900/5 border border-blue-100 flex justify-center gap-12 items-center w-full max-w-sm mx-auto">
                                                    <div className="text-center">
                                                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Účastníci</p>
                                                        <p className="text-3xl font-black text-blue-900 leading-none">{totalHistoricalParticipants}</p>
                                                    </div>
                                                    <div className="w-px h-12 bg-gray-200" />
                                                    <div className="text-center">
                                                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Posádky</p>
                                                        <p className="text-3xl font-black text-blue-900 leading-none">{totalHistoricalBoats}</p>
                                                    </div>
                                                </div>

                                                <h4 className="text-2xl font-black text-blue-900 uppercase tracking-widest mb-10 text-center border-b-4 border-yellow-400 pb-2 inline-block" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                    Najlepšie časy — {selectedYear}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full mb-12">
                                                    {Object.entries(byCategory).map(([catName, results]) => (
                                                        <div key={catName} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden flex flex-col transform hover:-translate-y-1 transition-transform">
                                                            <div className="bg-blue-900 py-3 px-4 text-center border-b-4 border-yellow-400">
                                                                <h4 className="text-white font-black uppercase tracking-widest text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>{catName}</h4>
                                                            </div>
                                                            <div className="p-4 flex flex-col gap-3">
                                                                {results.sort((a, b) => a.place - b.place).map((res, idx) => (
                                                                    <div key={idx} className="flex items-center gap-3 border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black shrink-0 shadow-sm text-sm ${medalStyle(idx)}`}>
                                                                            {res.place}
                                                                        </div>
                                                                        <div className="flex flex-col flex-grow">
                                                                            {Array.isArray(res.names) ? res.names.map((n, nIdx) => (
                                                                                <span key={nIdx} className="text-blue-900 font-bold text-sm leading-tight">{n}</span>
                                                                            )) : <span className="text-blue-900 font-bold text-sm">{res.names}</span>}
                                                                        </div>
                                                                        <div className="bg-gray-100 px-2 py-1 rounded text-gray-700 font-black text-xs shrink-0 border border-gray-200">{res.time}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {yearLinks[selectedYear] && (
                                                    <button
                                                        className="font-black uppercase tracking-widest px-8 py-4 shadow-xl transition-all transform -skew-x-12 hover:scale-105 inline-flex items-center gap-2 bg-blue-900 text-white hover:bg-blue-800"
                                                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                                                        onClick={() => setModalImg(yearLinks[selectedYear])}
                                                    >
                                                        <span className="transform skew-x-12">Celkové poradie {selectedYear}</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Image modal */}
            {modalImg && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setModalImg(null)}>
                    <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
                        <img src={modalImg} alt="Celkové poradie" className="w-full rounded-2xl shadow-2xl" />
                        <button className="absolute top-4 right-4 bg-white text-blue-900 font-black px-4 py-2 rounded-xl hover:bg-yellow-400" onClick={() => setModalImg(null)}>✕ Zatvoriť</button>
                    </div>
                </div>
            )}

            <style>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
}
